import { ArrowRight, ShieldCheck, Star, Truck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SITE } from "@/lib/site";
import QuoteForm from "@/components/site/QuoteForm";
import { SiciliaOutline, TrinacriaWatermark } from "@/components/site/decorations/Sicilia";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-[100px] sm:pt-[120px] lg:pt-[170px] pb-20 lg:pb-32 overflow-hidden"
    >
      <div className="hero-blob" />
      <div className="absolute right-0 top-1/3 w-[460px] h-[460px] dot-grid opacity-40 hidden lg:block" />
      <div className="absolute -left-32 top-40 w-[420px] h-[420px] rounded-full bg-brand-primary/[0.07] blur-3xl hidden lg:block" />

      {/* Decorative Sicilia silhouette — subtle background behind headline */}
      <SiciliaOutline
        className="absolute -right-16 top-1/4 w-[560px] text-brand-primary/[0.045] hidden lg:block pointer-events-none"
        fill="currentColor"
      />
      <TrinacriaWatermark className="absolute left-6 bottom-10 w-20 text-brand-primary/20 hidden lg:block pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        {/* LEFT — copy */}
        <div className="lg:col-span-7 lg:pr-4">
          <div
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand-primary/10 border border-brand-primary/20 text-[11px] tracking-[0.2em] uppercase font-bold text-brand-primary reveal"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Sicilia · Nord Italia · Tutta Italia
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display font-extrabold text-[38px] sm:text-[56px] lg:text-[82px] leading-[0.95] sm:leading-[0.92] tracking-tighter text-brand-ink mt-5 text-balance reveal reveal-delay-2"
          >
            Traslochi<br />
            in <span className="relative inline-block">
              <span className="relative z-10">Sicilia.</span>
              <span className="absolute left-0 right-0 bottom-1 h-3 lg:h-4 bg-brand-muted/70 -z-0 rounded-sm" />
            </span>{" "}
            <span className="block mt-2 text-brand-primary">Senza un graffio.</span>
            <span className="block text-brand-primary/80">Senza un pensiero.</span>
          </h1>

          <p
            data-testid="hero-subhead"
            className="mt-6 max-w-xl text-base sm:text-lg lg:text-xl text-brand-body leading-relaxed reveal reveal-delay-2"
          >
            <strong className="text-brand-ink">Siamo i migliori nel settore</strong> e non accettiamo compromessi sulla qualità.
            Ogni oggetto è coperto da <span className="text-brand-primary font-semibold">assicurazione completa</span> dal ritiro alla consegna.
            Dalla Sicilia a Milano. Senza un graffio.
          </p>

          {/* HUGE CTA */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3 reveal reveal-delay-3">
            <Button
              asChild
              data-testid="hero-primary-cta"
              className="group w-full sm:w-auto h-14 sm:h-16 px-6 sm:px-8 rounded-full bg-brand-primary hover:bg-brand-ink text-white text-base sm:text-lg font-bold animate-pulse-ring shadow-2xl shadow-brand-primary/30"
            >
              <a href="#preventivo" className="justify-center">
                Richiedi Preventivo Gratuito
                <ArrowRight className="ml-2 sm:ml-3 w-5 h-5 sm:w-6 sm:h-6 transition-transform group-hover:translate-x-1" />
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="hero-secondary-cta"
              className="w-full sm:w-auto h-14 sm:h-16 px-6 rounded-full border-2 border-brand-line hover:border-brand-primary hover:bg-brand-surface text-brand-ink text-sm sm:text-base font-bold"
            >
              <a href={`tel:${SITE.phoneTel}`} className="justify-center">📞 {SITE.phone}</a>
            </Button>
          </div>

          {/* Trust mini-row */}
          <div className="mt-10 grid grid-cols-3 gap-3 sm:gap-6 max-w-xl reveal reveal-delay-4">
            <TrustChip icon={<ShieldCheck className="w-5 h-5" />} title="100%" sub="Assicurati" />
            <TrustChip icon={<Star className="w-5 h-5" />} title="4.9★" sub="Recensioni" />
            <TrustChip icon={<Truck className="w-5 h-5" />} title="4.200+" sub="Traslochi" />
          </div>
        </div>

        {/* RIGHT — form */}
        <div className="lg:col-span-5 lg:sticky lg:top-32">
          <QuoteForm variant="hero" />
        </div>
      </div>
    </section>
  );
}

function TrustChip({ icon, title, sub }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-11 h-11 rounded-xl bg-brand-primary/10 text-brand-primary flex items-center justify-center">
        {icon}
      </div>
      <div>
        <div className="font-display font-extrabold text-2xl text-brand-ink leading-none">{title}</div>
        <div className="text-xs uppercase tracking-wider text-brand-body mt-1">{sub}</div>
      </div>
    </div>
  );
}
